import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// Rescisão → "Aviso/Carta": pedido da carta de desligamento de próprio punho ao
// colaborador. SÓ em pedido do empregado — o backend confere o tipo na Convenia
// (leitura) e bloqueia o resto. Este modal é a prévia: de/para/cc, assunto e o
// texto (editável como texto normal); o Enviar fica aqui dentro.
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const CartaDesligamento = ({ clienteId, conveniaId, colaborador, onFechar, onEnviado }) => {
  const [d, setD] = useState(null);             // resposta da prévia
  const [form, setForm] = useState(null);       // {operador_id, para, cc, assunto, razao_social, cnpj}
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [salvandoRazao, setSalvandoRazao] = useState(false);
  const [erro, setErro] = useState('');
  const [versao, setVersao] = useState(0);      // remonta o editor quando o texto é refeito
  const [corpo, setCorpo] = useState('');

  const carregar = useCallback((extra) => {
    setCarregando(true);
    setErro('');
    axios.get(`${API}/api/bporh/rescisao/carta/`,
      { params: { cliente_id: clienteId, convenia_id: conveniaId, ...(extra || {}) }, headers: headers() })
      .then((r) => {
        const x = r.data;
        setD(x);
        setForm((f) => ({
          operador_id: f?.operador_id || x.operador_sugerido_id || '',
          para: f?.para ?? (x.para || ''),
          cc: f?.cc ?? '',
          assunto: f?.assunto || x.assunto || '',
          razao_social: x.razao_social || '',
          cnpj: x.cnpj || '',
        }));
        setCorpo(x.corpo_html || '');
        setVersao((n) => n + 1);
      })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'))
      .finally(() => setCarregando(false));
  }, [clienteId, conveniaId]);
  useEffect(() => { carregar(); }, [carregar]);

  // razão social / CNPJ / quem assina mudam o texto: refaz a prévia com os valores da tela
  const refazerTexto = () => carregar({ razao_social: form.razao_social, cnpj: form.cnpj, operador_id: form.operador_id });

  const salvarRazao = () => {
    setSalvandoRazao(true);
    axios.post(`${API}/api/bporh/rescisao/carta/`,
      { acao: 'razao_social', cliente_id: clienteId, razao_social: form.razao_social }, { headers: headers() })
      .then(() => { setD((x) => ({ ...x, razao_social: form.razao_social })); refazerTexto(); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível gravar a razão social'))
      .finally(() => setSalvandoRazao(false));
  };

  const enviar = () => {
    setEnviando(true);
    setErro('');
    axios.post(`${API}/api/bporh/rescisao/carta/`, {
      acao: 'enviar', cliente_id: clienteId, convenia_id: conveniaId, operador_id: form.operador_id,
      destinatarios: form.para, cc: form.cc, assunto: form.assunto, corpo_html: corpo,
    }, { headers: headers() })
      .then((r) => onEnviado && onEnviado(r.data.mensagem))
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setEnviando(false));
  };

  const op = (d?.operadores || []).find((o) => String(o.id) === String(form?.operador_id));
  const textoDesatualizado = d && form
    && ((form.razao_social || '') !== (d.razao_social || '') || (form.cnpj || '') !== (d.cnpj || ''));
  const faltaDado = !form?.razao_social?.trim() || !form?.cnpj?.trim();

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
      onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content shadow-lg">
          <div className="modal-header bg-white">
            <div>
              <h5 className="modal-title" style={{ color: '#212529' }}>
                <i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>Pedir carta de desligamento
              </h5>
              <div className="small" style={{ color: '#6c757d' }}>
                {colaborador}{d?.cliente ? ` · ${d.cliente}` : ''}{d?.data_desligamento ? ` · desligamento ${d.data_desligamento}` : ''}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onFechar}></button>
          </div>

          <div className="modal-body">
            {carregando && !d && (
              <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2"></span>Conferindo o tipo do desligamento na Convenia…</div>
            )}
            {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

            {d && (
              <div className={`alert py-2 small ${d.permitido ? 'alert-success' : 'alert-warning'}`}>
                <i className={`fas ${d.permitido ? 'fa-circle-check' : 'fa-ban'} me-2`}></i>
                {d.permitido
                  ? <>Pedido do empregado confirmado ({d.origem_tipo}): <b>{d.tipo_desligamento}</b>
                    {d.aviso_data ? <> · aviso em {d.aviso_data}{d.aviso_tipo ? ` (${d.aviso_tipo})` : ''}</> : null}</>
                  : <><b>Carta não se aplica.</b> {d.motivo_bloqueio}</>}
              </div>
            )}
            {d?.ultimo_envio && (
              <div className="alert alert-info py-2 small">
                <i className="fas fa-clock-rotate-left me-2"></i>
                Carta já pedida em <b>{d.ultimo_envio.em}</b>{d.ultimo_envio.operador ? ` por ${d.ultimo_envio.operador}` : ''}
                {d.ultimo_envio.destinatarios ? ` para ${d.ultimo_envio.destinatarios}` : ''}. Enviar de novo manda outro e-mail.
              </div>
            )}

            {d?.permitido && form && (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">De (operadora que assina)</label>
                    <select className="form-select form-select-sm" value={form.operador_id}
                      onChange={(e) => setForm({ ...form, operador_id: e.target.value })}
                      onBlur={refazerTexto}>
                      <option value="">Selecione…</option>
                      {(d.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>
                          {o.nome}{o.email ? ` · ${o.email}` : ''}{o.pronto ? '' : ' (sem senha de app)'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">Para (e-mail pessoal)</label>
                    <input type="text" className="form-control form-control-sm" value={form.para}
                      onChange={(e) => setForm({ ...form, para: e.target.value })} />
                    {!d.para && <div className="form-text text-danger">Sem e-mail pessoal na Convenia — informe aqui.</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">Cc (opcional)</label>
                    <input type="text" className="form-control form-control-sm" value={form.cc} placeholder="separe por ;"
                      onChange={(e) => setForm({ ...form, cc: e.target.value })} />
                    {(d.grupo_cliente || []).length > 0 && (
                      <div className="form-text">Também vai {d.grupo_modo === 'bcc' ? 'em cópia oculta' : 'em cópia'} para o grupo do cliente: {d.grupo_cliente.join(', ')}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold mb-1">Assunto</label>
                    <input type="text" className="form-control form-control-sm" value={form.assunto}
                      onChange={(e) => setForm({ ...form, assunto: e.target.value })} />
                  </div>
                  <div className="col-md-7">
                    <label className="form-label small fw-semibold mb-1">Razão social do cliente</label>
                    <div className="input-group input-group-sm">
                      <input type="text" className="form-control" value={form.razao_social} placeholder="ex.: EMPRESA S.A"
                        onChange={(e) => setForm({ ...form, razao_social: e.target.value })} />
                      {(form.razao_social || '') !== (d.razao_social || '') && (
                        <button type="button" className="btn btn-outline-primary" disabled={salvandoRazao} onClick={salvarRazao}
                          title="Grava no cadastro do cliente: vale para os próximos pedidos">
                          {salvandoRazao ? <span className="spinner-border spinner-border-sm"></span> : 'Salvar'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-semibold mb-1">CNPJ (filial do colaborador)</label>
                    <input type="text" className="form-control form-control-sm" value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
                  </div>
                </div>
                {textoDesatualizado && (
                  <div className="d-flex align-items-center gap-2 small mb-2" style={{ color: '#8a6d00' }}>
                    <i className="fas fa-triangle-exclamation"></i>O texto ainda está com os dados anteriores.
                    <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={refazerTexto}>Atualizar texto</button>
                    <span className="text-muted">(refaz o texto; edições feitas nele se perdem)</span>
                  </div>
                )}
                <label className="form-label small fw-semibold mb-1">Texto do e-mail (clique para editar)</label>
                <div key={versao} className="border rounded p-3 bg-white" style={{ minHeight: 220, outline: 'none' }}
                  contentEditable suppressContentEditableWarning
                  ref={(el) => { if (el && !el.dataset.pronto) { el.innerHTML = corpo; el.dataset.pronto = '1'; } }}
                  onInput={(e) => setCorpo(e.currentTarget.innerHTML)} />
              </>
            )}
          </div>

          <div className="modal-footer bg-white">
            <button type="button" className="btn btn-secondary" onClick={onFechar}>Fechar</button>
            {d?.permitido && (
              <button type="button" className="btn btn-primary"
                disabled={enviando || carregando || !form?.operador_id || !form?.para?.trim() || faltaDado || textoDesatualizado}
                title={faltaDado ? 'Preencha a razão social e o CNPJ' : textoDesatualizado ? 'Atualize o texto antes de enviar' : ''}
                onClick={enviar}>
                {enviando
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando…</>
                  : <><i className="fas fa-paper-plane me-2"></i>Enviar{op ? ` como ${op.nome}` : ''}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartaDesligamento;
